import {
  Table,
  Button,
  Input,
  Modal,
  message,
  Space,
  Tooltip,
  Tag,
  Switch,
  Divider,
  Spin,
} from "antd";
import DatePicker, {
  TimePicker,
} from "../../../../../components/inputs/MomentDatePicker";
import React, { useState, useMemo } from "react";
import moment from "moment";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectUser } from "../../../../../redux/slices/auth.slice";
import {
  useGetInstructorAttendanceByDate,
  useCheckIn,
  useCheckOut,
  useUpdateAttendance,
} from "../../../../../services/geofencing.service";
import { fetchDailySummary } from "../../../../../services/report.service";
import { useStaffGroup } from "./StaffGroupContext";

const InstructorHomepage = () => {
  const group = useStaffGroup();
  const user = useSelector(selectUser);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [isLiveView, setIsLiveView] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: attendanceData,
    isLoading,
    refetch,
    isFetching,
  } = useGetInstructorAttendanceByDate(
    selectedDate?.format("YYYY-MM-DD"),
    {
      refetchInterval: isLiveView ? 60000 : false,
      refetchOnWindowFocus: isLiveView,
    },
    group,
  );

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [newCheckInTime, setNewCheckInTime] = useState(null);
  const [newCheckOutTime, setNewCheckOutTime] = useState(null);
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [selectedSummaryDetails, setSelectedSummaryDetails] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const navigate = useNavigate();

  const getErrorMessage = (error, defaultMsg) => {
    const data = error?.response?.data;

    // NestJS reports errors as { statusCode, message, error }, where message is
    // a string, or an array for validation failures. This only ever read
    // `detail`, so every server error surfaced as the generic default.
    const serverMessage = data?.message;
    if (typeof serverMessage === "string" && serverMessage) return serverMessage;
    if (Array.isArray(serverMessage) && serverMessage.length > 0) {
      return serverMessage.join(", ");
    }

    const detail = data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0]?.msg || defaultMsg;
    }
    return defaultMsg;
  };

  const [loadingInstructorAction, setLoadingInstructorAction] = useState({
    id: null,
    type: null,
  });

  const checkInMutation = useCheckIn({
    onMutate: (variables) => {
      setLoadingInstructorAction({
        id: variables.instructorId,
        type: "check-in",
      });
    },
    onSuccess: () => {
      message.success("Check-in successful");
      refetch();
    },
    onError: (error) => {
      message.error(getErrorMessage(error, "Check-in failed"));
    },
    onSettled: () => {
      setLoadingInstructorAction({ id: null, type: null });
    },
  });

  const checkOutMutation = useCheckOut({
    onMutate: (variables) => {
      setLoadingInstructorAction({
        id: variables.instructorId,
        type: "check-out",
      });
    },
    onSuccess: () => {
      message.success("Check-out successful");
      refetch();
    },
    onError: (error) => {
      message.error(getErrorMessage(error, "Check-out failed"));
    },
    onSettled: () => {
      setLoadingInstructorAction({ id: null, type: null });
    },
  });

  const updateAttendanceMutation = useUpdateAttendance({
    onSuccess: () => {
      message.success("Attendance updated successfully");
      setIsEditModalVisible(false);
      refetch();
    },
    onError: (error) => {
      message.error(getErrorMessage(error, "Update failed"));
    },
  });

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleCheckIn = (record) => {
    const schoolId = user?.school?._id || user?.schoolId || user?.school;

    if (!schoolId) {
      message.error("School ID not found. Please log in again.");
      return;
    }

    const payload = {
      instructorId: record.instructor_id?._id || record.instructor_id,
      schoolId: String(schoolId),
      latitude: 0.0,
      longitude: 0.0,
      accuracy: 0.0,
      timestamp: new Date().toISOString(),
      deviceId: "admin_web_portal",
      isMock: false,
    };

    checkInMutation.mutate(payload);
  };

  const handleCheckOut = (record) => {
    const attendanceId = record._id || record.id;
    if (!attendanceId) {
      message.error(
        "Attendance record ID missing from backend. Cannot check out.",
      );
      return;
    }

    const payload = {
      attendanceId: attendanceId,
      instructorId: record.instructor_id?._id || record.instructor_id,
      latitude: 0.0,
      longitude: 0.0,
      timestamp: new Date().toISOString(),
      deviceId: "admin_web_portal",
    };

    checkOutMutation.mutate(payload);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setNewCheckInTime(
      record.check_in_time ? moment(record.check_in_time) : null,
    );
    setNewCheckOutTime(
      record.check_out_time ? moment(record.check_out_time) : null,
    );
    setIsEditModalVisible(true);
  };

  const handleUpdateSubmit = () => {
    if (!editingRecord) return;

    const schoolId = user?.school?._id || user?.schoolId || user?.school;

    // Helper to combine the date from the record with the time from the picker
    const combineDateTime = (timeMoment) => {
      if (!timeMoment) return null;
      const datePart = editingRecord.date;
      if (!datePart) return timeMoment.toISOString();

      const combined = moment(datePart, "YYYY-MM-DD")
        .hour(timeMoment.hour())
        .minute(timeMoment.minute())
        .second(0);
      return combined.toISOString();
    };

    const finalCheckIn = combineDateTime(newCheckInTime);
    const finalCheckOut = combineDateTime(newCheckOutTime);

    if (!finalCheckIn && !finalCheckOut) {
      message.warning("Please set at least a Check-in or Check-out time");
      return;
    }

    // Both times are combined with the same record date above, so comparing
    // them directly is safe. The server enforces this too; checking here just
    // spares the admin a round trip.
    if (
      finalCheckIn &&
      finalCheckOut &&
      moment(finalCheckOut).isBefore(moment(finalCheckIn))
    ) {
      message.error("Check-out time cannot be earlier than check-in time");
      return;
    }

    updateAttendanceMutation.mutate({
      attendanceId: editingRecord._id || editingRecord.id || "undefined",
      data: {
        checkInTime: finalCheckIn,
        checkOutTime: finalCheckOut,
        instructorId: editingRecord.instructor_id,
        schoolId: String(schoolId),
        notes: "Admin manually updated via web portal",
      },
    });
  };

  const handleShowDailySummary = async () => {
    if (!selectedDate) return;
    setSummaryLoading(true);
    try {
      const dateStr = selectedDate.format("YYYY-MM-DD");
      const res = await fetchDailySummary(dateStr, group);
      if (res && res.success) {
        const d = res.data;
        setSelectedSummaryDetails({
          date: d.meta.date,
          day: d.meta.day,
          stats: {
            onTime: d.summary.on_time,
            missed: d.summary.missed,
            shortTime: d.summary.short_time,
            leave: d.summary.leave,
            sick: d.summary.sick,
            permission: d.summary.permission,
            holiday: d.summary.holiday,
          },
          detailsList: d.details,
        });
        setIsSummaryModalVisible(true);
      } else {
        message.info("No summary data available for this date");
      }
    } catch (error) {
      console.error("Failed to fetch summary:", error);
      message.error("Failed to fetch daily summary");
    } finally {
      setSummaryLoading(false);
    }
  };

  const detailColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Check In",
      dataIndex: "check_in_time",
      key: "check_in_time",
      render: (t) => t || "-",
    },
    {
      title: "Check Out",
      dataIndex: "check_out_time",
      key: "check_out_time",
      render: (t) => t || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => {
        const status = (text || "").toLowerCase();
        let color = "blue";
        if (status === "holiday") color = "pink";
        return (
          <Tag color={color} className="capitalize">
            {text ? text.replace("_", " ") : "-"}
          </Tag>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
  ];

  const getStatusDisplay = (record) => {
    const rawStatus = record.status;
    const status = (
      typeof rawStatus === "string" ? rawStatus : ""
    ).toLowerCase();

    let badgeColor = "default";
    let badgeLabel = "Waiting";
    let reasonLabel = "Off Hours";

    if (status === "") {
      return {
        badge: { color: "default", label: "Waiting" },
        reason: { label: "Off Hours", color: "default" },
      };
    }

    switch (status) {
      case "on_time":
        badgeLabel = "Present";
        badgeColor = "green";
        reasonLabel = "On Time";
        break;
      case "short_time":
        badgeLabel = "Present";
        badgeColor = "gold";
        reasonLabel = "Short Time";
        break;
      case "permission":
        badgeLabel = "Present";
        badgeColor = "blue";
        reasonLabel = "Permission";
        break;
      case "check_in":
        badgeLabel = "Present";
        badgeColor = "cyan";
        reasonLabel = "Excused Late";
        break;
      case "check_out":
        badgeLabel = "Present";
        badgeColor = "cyan";
        reasonLabel = "Excused Early";
        break;
      case "half_day":
        badgeLabel = "Half Day";
        badgeColor = "orange";
        reasonLabel = "Half Day";
        break;
      case "missed":
        badgeLabel = "Absent";
        badgeColor = "red";
        reasonLabel = "Missed";
        break;
      case "leave":
        badgeLabel = "Absent";
        badgeColor = "red";
        reasonLabel = "Leave";
        break;
      case "sick":
        badgeLabel = "Absent";
        badgeColor = "magenta";
        reasonLabel = "Sick";
        break;
      case "holiday":
        badgeLabel = "Holiday";
        badgeColor = "pink";
        reasonLabel = "Holiday";
        break;
      default:
        if (status) {
          reasonLabel =
            status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ");
          badgeLabel = reasonLabel;
          badgeColor = "blue";
        }
        break;
    }

    return {
      badge: { color: badgeColor, label: badgeLabel },
      reason: { label: reasonLabel, color: badgeColor },
    };
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      align: "center",
      width: 220,
      render: (record) => {
        const name = record.instructor_name || record.name;
        return (
          <Space>
            <div className="text-base font-semibold capitalize">
              {String(name || "Unknown Instructor")}
            </div>
            <Tooltip title="Edit Attendance">
              <Button
                type="text"
                icon={<EditOutlined className="text-blue-500" />}
                onClick={() => handleEditClick(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "Check In",
      key: "checkin",
      align: "center",
      width: 150,
      render: (record) => {
        const isPastDate =
          selectedDate && selectedDate.isBefore(moment(), "day");
        return (
          <div className="text-center">
            {record.check_in_time ? (
              <div className="font-medium text-green-600">
                {moment(record.check_in_time).isValid()
                  ? moment(record.check_in_time).format("hh:mm A")
                  : "-"}
              </div>
            ) : isPastDate ? (
              "-"
            ) : (
              <Button
                type="primary"
                size="small"
                className="bg-green-500 hover:bg-green-600 border-none"
                onClick={() => handleCheckIn(record)}
                loading={
                  loadingInstructorAction.type === "check-in" &&
                  String(loadingInstructorAction.id) ===
                    String(record.instructor_id?._id || record.instructor_id)
                }
              >
                Check In
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: "Check Out",
      key: "checkout",
      align: "center",
      width: 150,
      render: (record) => {
        const isPastDate =
          selectedDate && selectedDate.isBefore(moment(), "day");
        return (
          <div className="text-center">
            {record.check_out_time ? (
              <div className="font-medium text-red-600">
                {moment(record.check_out_time).isValid()
                  ? moment(record.check_out_time).format("hh:mm A")
                  : "-"}
              </div>
            ) : isPastDate ? (
              "-"
            ) : (
              <Button
                type="primary"
                size="small"
                danger
                disabled={!record.check_in_time}
                onClick={() => handleCheckOut(record)}
                loading={
                  loadingInstructorAction.type === "check-out" &&
                  String(loadingInstructorAction.id) ===
                    String(record.instructor_id?._id || record.instructor_id)
                }
              >
                Check Out
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: "Hours",
      key: "hours",
      align: "center",
      width: 100,
      render: (record) => {
        let hours = record.worked_hours ?? record.hours;
        if (typeof hours === "object" && hours !== null) {
          hours = hours.hours ?? hours.worked_hours;
        }
        return (
          <div className="text-center font-medium">
            {hours !== undefined && hours !== null ? `${hours} hrs` : "-"}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      width: 120,
      render: (record) => {
        const { badge } = getStatusDisplay(record);
        return (
          <div className="text-center">
            <Tag color={badge.color} className="rounded-full px-3">
              {String(badge.label || "")}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "Status Reason",
      key: "statusReason",
      align: "center",
      width: 200,
      render: (record) => {
        const { reason } = getStatusDisplay(record);
        return (
          <Space direction="vertical" size={0}>
            <Tag color={reason.color} className="border-none font-medium">
              {String(reason.label || "")}
            </Tag>
            {record.verification_source === "admin_manual" && (
              <span className="text-[10px] text-gray-400 font-italic">
                (Admin Override)
              </span>
            )}
          </Space>
        );
      },
    },
  ];

  const data = useMemo(() => {
    const list = Array.isArray(attendanceData) ? attendanceData : [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return list;
    return list.filter((record) =>
      String(record.instructor_name || record.name || "")
        .toLowerCase()
        .includes(query),
    );
  }, [attendanceData, searchQuery]);

  return (
    <div className="mt-8">
      <div className="bg-white drop-shadow-md p-8 w-full rounded-lg">
        <div className="flex items-center gap-4 py-4">
          <div className="text-2xl text-orange-500 font-semibold">
            Instructor Attendance
          </div>
          {selectedSummaryDetails?.stats?.holiday > 0 && (
            <Tag
              color="pink"
              className="text-lg px-4 py-1 rounded-full border-none shadow-sm animate-pulse"
            >
              🎉 School Holiday
            </Tag>
          )}
        </div>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <span className="text-base font-semibold">Select Date: &nbsp;</span>
            <DatePicker
              format="DD/MM/YYYY"
              value={selectedDate}
              onChange={handleDateChange}
              allowClear={false}
              style={{ width: 200 }}
              disabledDate={(current) =>
                current && current > moment().endOf("day")
              }
            />
            <Input
              placeholder="Search instructor"
              allowClear
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<SearchOutlined className="text-gray-400" />}
              style={{ width: 220 }}
            />
            <Button
              onClick={handleShowDailySummary}
              loading={summaryLoading}
              className="font-semibold"
            >
              Daily Summary
            </Button>
            <Divider type="vertical" />
            <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-sm">
              <span className="text-sm font-medium text-gray-700">
                <span className="mr-1">🔴</span> Live View:
              </span>
              <Switch
                size="small"
                checked={isLiveView}
                onChange={setIsLiveView}
                className={isLiveView ? "bg-green-500" : ""}
              />
              {isFetching && isLiveView && (
                <div className="flex items-center ml-3 pl-3 border-l border-gray-200">
                  <Spin size="small" className="mr-2" />
                  <span className="text-xs text-blue-600 font-medium">
                    Updating...
                  </span>
                </div>
              )}
            </div>
          </div>

          <Button
            type="primary"
            size="middle"
            style={{ padding: "4px 16px" }}
            onClick={() =>
              navigate(
                "/dashboard/admin/menu/instructor-attendance/report-status",
              )
            }
          >
            Status Report
          </Button>
        </div>

        <div className="py-5">
          <Table
            rowKey={(record) =>
              record._id || record.instructor_id?._id || record.instructor_id
            }
            columns={columns}
            dataSource={data?.length > 0 ? data : []}
            loading={isLoading}
            rowClassName="hover:bg-gray-50"
          />
        </div>
        <Modal
          title={`Edit Attendance: ${editingRecord?.instructor_name}`}
          visible={isEditModalVisible}
          onOk={handleUpdateSubmit}
          onCancel={() => setIsEditModalVisible(false)}
          confirmLoading={updateAttendanceMutation.isLoading}
        >
          <div className="flex flex-col gap-4 py-4">
            <div>
              <div className="mb-2 font-medium">Check-in Time:</div>
              <TimePicker
                use12Hours
                format="hh:mm A"
                value={newCheckInTime}
                onChange={(time) => setNewCheckInTime(time)}
                className="w-full"
              />
            </div>
            <div>
              <div className="mb-2 font-medium">Check-out Time:</div>
              <TimePicker
                use12Hours
                format="hh:mm A"
                value={newCheckOutTime}
                onChange={(time) => setNewCheckOutTime(time)}
                className="w-full"
              />
            </div>
          </div>
        </Modal>

        <Modal
          title={null}
          visible={isSummaryModalVisible}
          onCancel={() => setIsSummaryModalVisible(false)}
          footer={null}
          width={700}
        >
          {selectedSummaryDetails && (
            <div>
              <div className="text-lg font-bold mb-4 border-b pb-2">
                Summary:{" "}
                {moment(selectedSummaryDetails.date).format("DD/MM/YYYY")},{" "}
                {selectedSummaryDetails.day}
              </div>

              <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-base">
                  <div>
                    On Time:{" "}
                    <span className="font-semibold">
                      {selectedSummaryDetails.stats.onTime}
                    </span>
                  </div>
                  <div>
                    Missed:{" "}
                    <span className="font-semibold">
                      {selectedSummaryDetails.stats.missed}
                    </span>
                  </div>
                  <div>
                    Short Time:{" "}
                    <span className="font-semibold">
                      {selectedSummaryDetails.stats.shortTime}
                    </span>
                  </div>
                  <div>
                    Leave:{" "}
                    <span className="font-semibold">
                      {selectedSummaryDetails.stats.leave}
                    </span>
                  </div>
                  <div>
                    Sick:{" "}
                    <span className="font-semibold">
                      {selectedSummaryDetails.stats.sick}
                    </span>
                  </div>
                  <div>
                    Permission:{" "}
                    <span className="font-semibold">
                      {selectedSummaryDetails.stats.permission}
                    </span>
                  </div>
                  <div>
                    Holiday:{" "}
                    <span className="font-semibold">
                      {selectedSummaryDetails.stats.holiday}
                    </span>
                  </div>
                </div>
              </div>

              <div className="font-semibold mb-2">Details:</div>
              <Table
                dataSource={selectedSummaryDetails.detailsList}
                columns={detailColumns}
                pagination={false}
                size="small"
                rowKey="name"
              />
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default InstructorHomepage;
